import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { GetActiveRecords, RemoveActiveRecord, SetActiveCheckout } from './active-records.actions';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import type { LoadingModel } from 'src/app/_core/state/loading.model';
import { ActiveRecordsService } from './service/active-records.service';
import { tap, type Observable } from 'rxjs';
import type { ActiveRecordsModel, ActiveRecordWithColors } from './models/active-records.model';
import { CheckoutsFacade } from '../user/checkouts/checkouts.facade';

export type ActiveRecordsStateModel = LoadingModel & {
  activeRecords: ActiveRecordWithColors[];
  canMakeCheckouts: boolean;
};

export const initialState: ActiveRecordsStateModel = {
  activeRecords: [],
  canMakeCheckouts: false,
  isLoading: false,
};

@State<ActiveRecordsStateModel>({
  name: 'activeRecords',
  defaults: initialState,
})
@Injectable()
export class ActiveRecordsState {
  private readonly activeRecordsService = inject(ActiveRecordsService);
  private readonly checkoutsFacade = inject(CheckoutsFacade);

  @Selector() static activeRecords(state: ActiveRecordsStateModel) {
    return state.activeRecords;
  }

  @Selector() static canMakeCheckouts(state: ActiveRecordsStateModel) {
    return state.canMakeCheckouts;
  }

  @Selector() static isLoading(state: ActiveRecordsStateModel) {
    return state.isLoading;
  }

  @Action(GetActiveRecords)
  getActiveRecords(context: StateContext<ActiveRecordsStateModel>): Observable<ActiveRecordsModel> {
    context.setState(
      patch({
        isLoading: true,
      })
    );

    return this.activeRecordsService.getActiveRecords().pipe(
      tap((response) => {
        context.setState(
          patch<ActiveRecordsStateModel>({
            activeRecords: response.activeRecords,
            canMakeCheckouts: response.canMakeCheckouts,
            isLoading: false,
          })
        );

        this.syncCheckouts(response.activeRecords);
      })
    );
  }

  @Action(RemoveActiveRecord)
  removeActiveRecord(context: StateContext<ActiveRecordsStateModel>, { checkoutId }: RemoveActiveRecord) {
    context.setState(
      patch<ActiveRecordsStateModel>({
        activeRecords: removeItem<ActiveRecordWithColors>(
          (activeRecord) => activeRecord.checkoutId?.toString() === checkoutId
        ),
      })
    );
  }

  @Action(SetActiveCheckout)
  setActiveCheckout(context: StateContext<ActiveRecordsStateModel>, { checkout }: SetActiveCheckout) {
    context.setState(
      patch<ActiveRecordsStateModel>({
        activeRecords: updateItem<ActiveRecordWithColors>(
          // We use recordId because checkoutId changes after the checkout is renewed
          (item) => String(item.recordId) === String(checkout.recordId),
          (item) => ({
            ...item,
            checkoutId: checkout.id,
            timeLeft: Number(checkout.endTime),
          })
        ),
      })
    );
  }

  private syncCheckouts(records: ActiveRecordWithColors[]): void {
    const activeCheckoutsIds = records.filter((r) => r.checkoutId).map((r) => r.recordId);
    const facadeIds = this.checkoutsFacade.checkouts.map((c) => c.recordId);
    const isSynced = activeCheckoutsIds.every((id) => facadeIds.includes(id));

    if (!isSynced) {
      this.checkoutsFacade.getCheckouts();
    }
  }
}
